import { decodeColbin } from './colbin'
import { ref, onValue, query, orderByKey, limitToLast, get, off, startAt, endAt } from 'firebase/database'
import { getFirebaseDatabase } from './config'
import { BoatData, ParsedBoatData } from '../types'
import { getToken } from '../api/auth'

const MYBOATS_API = (import.meta as any).env.VITE_API_URL ?? ''

/**
 * Parse OLD-format boat data string ("KEY:VALUE KEY:VALUE ...") into structured object.
 * Kept for backward compatibility with legacy records.
 */
function parseBoatData(dataString: string): ParsedBoatData {
    const parsed: ParsedBoatData = {}

    // Pre-process: remove spaces after colons (e.g. "pH: 10.75" -> "pH:10.75")
    const processedString = dataString.replace(/: /g, ':')
    const parts = processedString.split(' ')
    for (const part of parts) {
        if (!part || part === '|') continue

        const [key, value] = part.split(':')
        if (!key || value === undefined) continue

        let lowerKey = key.toLowerCase()

        // Handle special mappings
        if (lowerKey === 'alt_msl') lowerKey = 'altMsl'
        if (lowerKey === 'alt_elli') lowerKey = 'altElli'
        if (lowerKey === 'wifi_lvl') lowerKey = 'wifiLvl'
        if (lowerKey === 'wifi_dbm') lowerKey = 'wifiDbm'
        if (lowerKey === 'wifi_link') lowerKey = 'wifiLink'
        if (lowerKey === 'cell_tech') lowerKey = 'cellTech'
        if (lowerKey === 'cell_lvl') lowerKey = 'cellLvl'
        if (lowerKey === 'cell_dbm') lowerKey = 'cellDbm'
        if (lowerKey === 'nh4+') lowerKey = 'nh4'

        const cleanValue = value.replace(/[^\d.-]/g, '')

        if (cleanValue !== '' && !isNaN(parseFloat(cleanValue))) {
            parsed[lowerKey] = parseFloat(cleanValue)
        } else {
            parsed[lowerKey] = value
        }
    }

    return parsed
}

/**
 * Convert a NEW-format structured record (JSON object) into ParsedBoatData.
 * Maps snake_case fields from the schema to camelCase used in the frontend.
 */
function structuredToParsed(record: any): ParsedBoatData {
    const parsed: ParsedBoatData = {}

    // Direct mappings
    if (record.lat !== undefined) parsed.lat = Number(record.lat)
    if (record.lon !== undefined) parsed.lon = Number(record.lon)
    if (record.raw_lat !== undefined) parsed.raw_lat = Number(record.raw_lat)
    if (record.raw_lon !== undefined) parsed.raw_lon = Number(record.raw_lon)
    if (record.bat !== undefined) parsed.bat = Number(record.bat)
    if (record.time !== undefined) parsed.time = record.time
    if (record.geohash !== undefined) parsed.geohash = record.geohash

    // IMU
    if (record.gx !== undefined) parsed.gx = Number(record.gx)
    if (record.gy !== undefined) parsed.gy = Number(record.gy)
    if (record.gz !== undefined) parsed.gz = Number(record.gz)
    if (record.ax !== undefined) parsed.ax = Number(record.ax)
    if (record.ay !== undefined) parsed.ay = Number(record.ay)
    if (record.az !== undefined) parsed.az = Number(record.az)
    if (record.hea !== undefined) parsed.hea = Number(record.hea)
    if (record.pit !== undefined) parsed.pit = Number(record.pit)
    if (record.roll !== undefined) parsed.roll = Number(record.roll)

    // Sensors (snake_case → camelCase where needed)
    if (record.temp_c !== undefined) parsed.temp = Number(record.temp_c)
    if (record.ph !== undefined) parsed.ph = Number(record.ph)
    if (record.do_mg_l !== undefined) parsed.o2 = Number(record.do_mg_l)
    if (record.turb !== undefined) parsed.turbidity = Number(record.turb)
    if (record.phpa !== undefined) parsed.phpa = Number(record.phpa)
    if (record.alt_msl !== undefined) parsed.altMsl = Number(record.alt_msl)
    if (record.alt_elli !== undefined) parsed.altElli = Number(record.alt_elli)
    // GPS accuracy (EKF-fused, metres) — feeds the map's position gate.
    if (record.acc_m !== undefined) parsed.acc = Number(record.acc_m)
    // Correction PROVENANCE, mapped explicitly rather than left to the generic
    // pass-through below: the map decides from these whether it may claim carrier-phase
    // centimetres, and a silently dropped field turns into a false accuracy claim. The
    // algorithm matters as much as the method — `litpos_only` covers both a real PPK
    // solve and litpos-diff-v1, which is code-differential plus smoothing.
    if (record.corr !== undefined) parsed.corr = record.corr
    if (record.corr_algo !== undefined) parsed.corr_algo = record.corr_algo
    // The solver's own sigma, kept apart from `acc`: for the smoothed path it measures
    // smoothness, not truth, and must never be shown as accuracy.
    if (record.sigma_m !== undefined) parsed.sigma_m = Number(record.sigma_m)

    // Weather
    if (record.wt !== undefined) parsed.wt = Number(record.wt)
    if (record.wrh !== undefined) parsed.wrh = Number(record.wrh)
    if (record.wcl !== undefined) parsed.wcl = Number(record.wcl)

    // Wind sensors
    if (record.wind !== undefined) parsed.wind = Number(record.wind)
    if (record.wind_kmh !== undefined) parsed.wind_kmh = Number(record.wind_kmh)
    if (record.wind_raw !== undefined) parsed.wind_raw = Number(record.wind_raw)

    // Network
    if (record.wifi_lvl !== undefined) parsed.wifiLvl = Number(record.wifi_lvl)
    if (record.wifi_dbm !== undefined) parsed.wifiDbm = Number(record.wifi_dbm)
    if (record.wifi_link !== undefined) parsed.wifiLink = Number(record.wifi_link)
    if (record.cell_tech !== undefined) parsed.cellTech = record.cell_tech
    if (record.cell_lvl !== undefined) parsed.cellLvl = Number(record.cell_lvl)
    if (record.cell_dbm !== undefined) parsed.cellDbm = Number(record.cell_dbm)

    // Pass through any extra fields not explicitly mapped
    for (const k of Object.keys(record)) {
        if (parsed[k] === undefined && k !== 'schema_v' && k !== 'status') {
            parsed[k] = record[k]
        }
    }

    return parsed
}

/**
 * Detect whether a record is new-format (structured JSON) or old-format (string blob).
 * New format: has schema_v or has `lat` as a direct number field (not inside a string).
 * Old format: has a `data` field that is a string.
 */
function isNewFormat(record: any): boolean {
    if (record.schema_v !== undefined) return true
    if (typeof record.lat === 'number' && typeof record.lon === 'number') return true
    if (typeof record.data === 'string') return false
    return true // default to new if unsure
}

/**
 * Fetch all available boat IDs from the database.
 * Checks both `/latest` (new schema) and `/boats` (old schema).
 */
export async function fetchAvailableBoats(): Promise<string[]> {
    // The server intersects ITS OWN boat inventory with this user's allowlist. Two
    // different empties hide behind that: "you are allowed none" and "this server has no
    // boat data" — and today it is always the second, because the phones stream to Nida
    // rather than to this server's ingest, so its inventory is empty while the fleet is
    // alive in Firebase. Treating that as authoritative showed an empty map to everyone,
    // admin included, the moment the endpoint became reachable over https.
    //
    // So: a non-empty list is authoritative. An empty list with inventory 0 means "look
    // elsewhere" — we fall through to Firebase, and still apply the user's allowlist to
    // whatever we discover, so a restricted user stays restricted.
    let allowFilter: Set<string> | null = null
    const token = getToken()
    if (token) {
        try {
            const res = await fetch(`${MYBOATS_API}/api/my-boats`, { headers: { Authorization: `Bearer ${token}` } })
            if (res.ok) {
                const body = await res.json()
                // Old servers answer with a bare array; new ones with {boats, inventory, ...}
                const boats: string[] = Array.isArray(body) ? body : (body.boats ?? [])
                const inventory: number = Array.isArray(body) ? boats.length : (body.inventory ?? boats.length)
                if (boats.length > 0 || inventory > 0) return boats
                if (!Array.isArray(body) && body.restricted && Array.isArray(body.allow)) {
                    allowFilter = new Set<string>(body.allow)
                }
            }
        } catch (e) {
            console.warn('[fetchAvailableBoats] /api/my-boats failed, falling back to Firebase:', e)
        }
    }

    const boatIds = new Set<string>()
    const finish = (ids: string[]) => (allowFilter ? ids.filter(id => allowFilter!.has(id)) : ids)

    // Our server's boats (positions or water rows in the last 30 days) come first; Firebase adds the rest.
    const source = getDataSource()
    if (source !== 'firebase') {
        try {
            const body = await nidaGet('/v1/boats/seen?days=30')
            for (const id of (body?.boats ?? []) as string[]) boatIds.add(id)
        } catch (e) { console.warn('[fetchAvailableBoats] our server failed:', e) }
        if (source === 'server') return finish([...boatIds].sort())
    }

    const db = getFirebaseDatabase()
    if (!db) return finish([...boatIds].sort())

    try {
        // 1. /latest — one small object, one record per boat; the complete modern answer.
        const latestSnap = await get(ref(db, 'latest'))
        if (latestSnap.exists()) {
            for (const key of Object.keys(latestSnap.val())) boatIds.add(key)
        }
        if (latestSnap.exists()) return finish([...boatIds].sort())

        // 2. Fallback for databases that predate /latest — KEY NAMES ONLY, via the REST
        // API's ?shallow=true. The previous get(ref(db, 'boats')) pulled the ENTIRE
        // measurement history of every boat (306 MB measured in production, 2026-08-02)
        // and parsed it on the main thread just to read top-level key names. Its cost
        // grew with total history forever — this is what made the site increasingly
        // laggy as data accumulated.
        const base = (db.app.options as any).databaseURL?.replace(/\/$/, '')
        if (!base) return []
        const r = await fetch(`${base}/boats.json?shallow=true`)
        const keys = r.ok ? Object.keys((await r.json()) ?? {}) : []
        // Legacy flat structure (boats -> pushId -> {...}): push ids all start with '-'
        if (keys.length && keys.every(k => k.startsWith('-'))) return finish(['default'])
        for (const k of keys) boatIds.add(k)

        return finish([...boatIds].sort())
    } catch (error) {
        console.error('Error fetching boats:', error)
        return []
    }
}

// Where data is read from (owner, 2026-09-26: "r0.lt should show all data from the postgre of our server and
// only fall back if none found by session id from firebase"):
//   'server'   — DEFAULT (owner, 2026-10-06: "Firebase default off, both default Postgres"). Our server (Nida Postgres,
//                api.tack.coach) only — no Firebase connection is made unless someone switches to 'auto' or 'firebase'.
//   'auto'     — our server first; Firebase only when the server has nothing for that boat / time range / session.
//   'firebase' — Firebase only (the backup copy the phones also write).
// Persisted under a NEW key, so every browser starts on the new default instead of an earlier pick.
export type DataSource = 'firebase' | 'server' | 'auto'
const SOURCE_KEY = 'dataSourceV3'
export function getDataSource(): DataSource {
    try {
        const s = localStorage.getItem(SOURCE_KEY)
        if (s === 'server' || s === 'firebase' || s === 'auto') return s
    } catch { /* ignore */ }
    return 'server'
}
export function setDataSource(s: DataSource) { try { localStorage.setItem(SOURCE_KEY, s) } catch { /* ignore */ } }

// Our server's boat API (Nida backend/app/boat_feed.py) — answers in the Firebase record field names.
const NIDA_API = String((import.meta as any).env.VITE_NIDA_API ?? 'https://api.tack.coach').replace(/\/$/, '')
async function nidaGet(path: string): Promise<any> {
    const r = await fetch(`${NIDA_API}${path}`)
    if (!r.ok) throw new Error(`${path} -> HTTP ${r.status}`)
    return r.json()
}
const toFrame = (boatId: string) => (rec: any): BoatData =>
    ({ boatId, timestamp: Number(rec.ts) || 0, parsed: structuredToParsed(rec) })

export interface HistoryLayers { phone: BoatData[]; ppk: BoatData[] }

/**
 * Past-run playback for SEVERAL boats in one request (Nida docs/105): the phone layer (sensors — drives the frames)
 * and the PPK layer of every boat from /v1/boats/history-bin, a columnar binary the server gzips. It replaces
 * two JSON requests per boat (about 25 MB for a four-boat 3-hour run, ~1.4 MB this way) and decodes to exactly the
 * rows those requests returned. `sessionId` pins the FIRST boat, as the per-boat calls did. `exact` asks for float64
 * columns (no quantisation): the rows then equal the JSON endpoint's to the last bit — used for CSV downloads.
 *
 * Returns null — and the caller falls back to the per-boat requests it always used — when the data source is
 * Firebase-only, an id cannot be sent this way, or anything about the request or the decoding goes wrong. A boat the
 * server failed on or knows nothing about is simply absent from the result.
 */
export async function fetchHistoryBatch(
    boatIds: string[], startTime: number, endTime: number, sessionId?: string, opts?: { exact?: boolean }
): Promise<Record<string, HistoryLayers> | null> {
    if (boatIds.length === 0 || getDataSource() === 'firebase') return null
    if (boatIds.some(b => /[:,]/.test(b))) return null
    try {
        const parts = boatIds.map((b, i) => encodeURIComponent(b) + (i === 0 && sessionId ? ':' + encodeURIComponent(sessionId) : ''))
        const r = await fetch(`${NIDA_API}/v1/boats/history-bin?boats=${parts.join(',')}&from=${startTime}&to=${endTime}${opts?.exact ? '&exact=1' : ''}`)
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const c = decodeColbin(await r.arrayBuffer())
        for (const [b, msg] of Object.entries(c.errors)) console.warn(`[fetchHistoryBatch] ${b}: ${msg}`)
        const out: Record<string, HistoryLayers> = {}
        for (const b of boatIds) {
            const layers = c.boats[b]
            if (!layers) continue
            out[b] = {
                phone: (layers.phone ?? []).map(toFrame(b)).sort((x, y) => x.timestamp - y.timestamp),
                ppk: (layers.ppk ?? []).map(toFrame(b)),
            }
        }
        console.log(`[fetchHistoryBatch] ${Object.keys(out).length}/${boatIds.length} boats from our server in one request`)
        return out
    } catch (e) {
        console.warn('[fetchHistoryBatch] falling back to per-boat requests:', e)
        return null
    }
}

/** A live listener: our server polled every 2 s; in 'auto' Firebase only speaks while the server has had
 *  nothing fresh for this boat for 15 s (the boat is not streaming to us, or we are unreachable). */
export function subscribeLiveData(boatId: string, onData: (data: BoatData) => void, forceSource?: DataSource): () => void {
    const source = forceSource ?? getDataSource()
    let lastTs = 0
    let serverSeenAt = 0
    const emit = (d: BoatData) => { if (d.timestamp >= lastTs) { lastTs = d.timestamp; onData(d) } }
    const unsubs: (() => void)[] = []
    if (source === 'server' || source === 'auto') unsubs.push(subscribeServer(boatId, d => { serverSeenAt = Date.now(); emit(d) }))
    if (source === 'firebase') unsubs.push(subscribeFirebase(boatId, emit))
    if (source === 'auto') unsubs.push(subscribeFirebase(boatId, d => { if (Date.now() - serverSeenAt > 15_000) emit(d) }))
    return () => unsubs.forEach(fn => fn())
}

/**
 * The "who is transmitting now" layer for EVERY listed boat (live banner, map ghosts, Live-first list): one
 * poll of our server's /v1/boats/live-all every 3 s instead of a poll per boat; in 'auto' a boat the server
 * has had nothing fresh for in 15 s falls back to its Firebase listener. It used to be Firebase-only, which
 * made a live Group session (no positions in Firebase) invisible here (owner, 2026-09-26).
 */
export function subscribeAllLive(boatIds: string[], onData: (boatId: string, data: BoatData) => void): () => void {
    const source = getDataSource()
    const unsubs: (() => void)[] = []
    if (source === 'firebase') {
        for (const id of boatIds) unsubs.push(subscribeFirebase(id, d => onData(id, d)))
        return () => unsubs.forEach(fn => fn())
    }
    const serverSeenAt: Record<string, number> = {}
    const lastTs: Record<string, number> = {}
    const emit = (id: string, d: BoatData) => { if (d.timestamp >= (lastTs[id] ?? 0)) { lastTs[id] = d.timestamp; onData(id, d) } }
    const poll = async () => {
        try {
            const body = await nidaGet('/v1/boats/live-all')
            for (const [id, rec] of Object.entries((body?.boats ?? {}) as Record<string, any>)) {
                if (!rec || typeof rec.lat !== 'number') continue
                serverSeenAt[id] = Date.now()
                emit(id, toFrame(id)(rec))
            }
        } catch { /* ignore — Firebase covers in 'auto' */ }
    }
    poll()
    const timer = setInterval(poll, 3000)
    unsubs.push(() => clearInterval(timer))
    if (source === 'auto') for (const id of boatIds)
        unsubs.push(subscribeFirebase(id, d => { if (Date.now() - (serverSeenAt[id] ?? 0) > 15_000) emit(id, d) }))
    return () => unsubs.forEach(fn => fn())
}

/** Every live boat's BEST-AVAILABLE recent track from our server (live PPK / corrected where solved, the phone's
 *  fixes only after the corrected coverage ends — exactly how go.tack.coach draws it). Points are
 *  [ts, lon, lat, q] with q = 'p' carrier-phase, 'i' improved, 'g' phone GPS. {} for source 'firebase'. */
export type LiveTrack = { pts: [number, number, number, string][], corr_end: number }
export async function fetchLiveTracks(seconds = 1800): Promise<Record<string, LiveTrack>> {
    if (getDataSource() === 'firebase') return {}
    try {
        const body = await nidaGet(`/v1/boats/live-tracks?seconds=${seconds}`)
        return (body?.boats ?? {}) as Record<string, LiveTrack>
    } catch { return {} }
}

/** Poll our server's freshest record for a boat ({} when nothing in the last 10 min). */
function subscribeServer(boatId: string, onData: (data: BoatData) => void): () => void {
    const poll = async () => {
        try {
            const rec = await nidaGet(`/v1/boats/${encodeURIComponent(boatId)}/live`)
            if (rec && typeof rec.lat === 'number') onData(toFrame(boatId)(rec))
        } catch { /* ignore — Firebase covers in 'auto' */ }
    }
    poll()
    const id = setInterval(poll, 2000)
    return () => clearInterval(id)
}

/** Firebase live listener: /latest/{boatId} (new schema), /boats/{boatId} limitToLast(1) (old schema). */
function subscribeFirebase(
    boatId: string,
    onData: (data: BoatData) => void
): () => void {
    const db = getFirebaseDatabase()
    if (!db) return () => { }

    const unsubscribers: (() => void)[] = []

    // --- Primary: /latest/{boatId} ---
    const latestRef = ref(db, `latest/${boatId}`)
    console.log(`[subscribeLiveData] Listening to /latest/${boatId}`)

    // Track the most recent timestamp we've delivered, so fallback can't overwrite fresh data
    let lastDeliveredTs = 0

    onValue(latestRef, (snapshot: any) => {
        if (snapshot.exists()) {
            const record = snapshot.val()
            const parsed = structuredToParsed(record)
            // Derive timestamp: prefer ts, then parse time string, never Date.now()
            let timestamp = record.ts
            if (!timestamp && record.time && typeof record.time === 'string') {
                const d = new Date(record.time)
                if (!isNaN(d.getTime())) timestamp = d.getTime()
            }
            if (!timestamp) return // skip frames with no valid timestamp
            lastDeliveredTs = Math.max(lastDeliveredTs, timestamp)
            onData({
                boatId,
                timestamp,
                parsed
            })
        }
    })
    unsubscribers.push(() => off(latestRef))

    // --- Fallback: /boats/{boatId} (old schema) ---
    const path = boatId === 'default' ? 'boats' : `boats/${boatId}`
    const boatRef = ref(db, path)
    const q = query(boatRef, orderByKey(), limitToLast(1))

    console.log(`[subscribeLiveData] Also listening to ${path} (fallback)`)

    onValue(q, (snapshot: any) => {
        if (snapshot.exists()) {
            const data = snapshot.val()
            const keys = Object.keys(data)
            if (keys.length > 0) {
                const latestKey = keys[0]
                const record = data[latestKey]

                // Skip if this is a meta/measurements node (new schema boat — handled by /latest)
                if (record && record.measurements) return
                if (record && record.meta) return

                const timestamp = record.ts || parseInt(latestKey) || 0
                if (!timestamp || timestamp <= lastDeliveredTs) return // skip stale fallback data
                lastDeliveredTs = timestamp
                const rawData = record.data || record

                let parsed: ParsedBoatData = {}
                if (typeof rawData === 'string') {
                    parsed = parseBoatData(rawData)
                } else if (isNewFormat(record)) {
                    parsed = structuredToParsed(record)
                }

                onData({
                    boatId,
                    timestamp,
                    data: typeof rawData === 'string' ? rawData : undefined,
                    parsed
                })
            }
        }
    })
    unsubscribers.push(() => off(boatRef))

    return () => {
        unsubscribers.forEach(fn => fn())
    }
}

/**
 * Helper to extract timestamp from old-format records.
 */
function extractTimestamp(key: string, record: any, lastKnownDate: string | null): { ts: number, dateStr: string | null } {
    if (record.ts) {
        const date = new Date(record.ts)
        const dateStr = date.toISOString().split('T')[0]
        return { ts: record.ts, dateStr }
    }

    const keyTs = parseInt(key)
    if (!isNaN(keyTs) && keyTs > 1000000000000) {
        const date = new Date(keyTs)
        const dateStr = date.toISOString().split('T')[0]
        return { ts: keyTs, dateStr }
    }

    const dataStr = record.data || (typeof record === 'string' ? record : '')
    if (typeof dataStr === 'string') {
        const timeMatch = dataStr.match(/TIME:(\S+)/)
        if (timeMatch) {
            const timeVal = timeMatch[1]
            if (timeVal.includes('T')) {
                const ts = new Date(timeVal).getTime()
                if (!isNaN(ts)) {
                    return { ts, dateStr: timeVal.split('T')[0] }
                }
            }
            if (lastKnownDate && /^\d{2}:\d{2}:\d{2}$/.test(timeVal)) {
                const fullStr = `${lastKnownDate}T${timeVal}`
                const ts = new Date(fullStr).getTime()
                if (!isNaN(ts)) {
                    return { ts, dateStr: lastKnownDate }
                }
            }
        }
    }

    return { ts: NaN, dateStr: lastKnownDate }
}

/**
 * Fetch historical data for playback.
 * Tries new schema (/boats/{id}/measurements) first, then falls back to old schema.
 */
export async function fetchHistoricalData(
    boatId: string,
    startTime: number,
    endTime: number,
    forceSource?: DataSource | 'ppk',
    sessionId?: string
): Promise<BoatData[]> {
    const records: BoatData[] = []
    // A session picked from our server's list is asked for BY ITS ID; a hand-typed range spans every session.
    const q = `from=${startTime}&to=${endTime}` + (sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : '')

    // PPK layer: our server's corrected track alone. Honestly empty when nothing is solved — never
    // repainted with phone GPS (playback loads it next to the position frames as its own layer).
    if (forceSource === 'ppk') {
        try {
            const rows = await nidaGet(`/v1/boats/${encodeURIComponent(boatId)}/history?${q}&layer=ppk`)
            return (Array.isArray(rows) ? rows : []).map(toFrame(boatId))
        } catch (e) { console.warn('[fetchHistoricalData] PPK layer failed:', e); return [] }
    }

    // Our server first: positions (PPK-corrected where solved, phone kept as pre_lat/pre_lon) with the water
    // readings merged in. Firebase only when the server has nothing for this boat in this range.
    const source = forceSource ?? getDataSource()
    if (source === 'server' || source === 'auto') {
        try {
            const rows = await nidaGet(`/v1/boats/${encodeURIComponent(boatId)}/history?${q}`)
            if (Array.isArray(rows) && rows.length > 0) {
                console.log(`[fetchHistoricalData] ${rows.length} records from our server`)
                return rows.map(toFrame(boatId)).sort((a, b) => a.timestamp - b.timestamp)
            }
        } catch (e) { console.warn('[fetchHistoricalData] our server failed:', e) }
        if (source === 'server') return records
        console.log('[fetchHistoricalData] nothing on our server — falling back to Firebase')
    }

    const db = getFirebaseDatabase()
    if (!db) return records

    try {
        // --- Try new schema: /boats/{id}/measurements ---
        const measRef = ref(db, `boats/${boatId}/measurements`)
        const measQuery = query(
            measRef,
            orderByKey(),
            startAt(String(startTime)),
            endAt(String(endTime))
        )
        const measSnap = await get(measQuery)

        if (measSnap.exists()) {
            const data = measSnap.val()
            for (const [tsKey, record] of Object.entries(data) as [string, any][]) {
                const ts = record.ts || parseInt(tsKey)
                records.push({
                    boatId,
                    timestamp: ts,
                    parsed: structuredToParsed(record)
                })
            }
            console.log(`[fetchHistoricalData] Found ${records.length} records from measurements/`)
        }

        // --- Corrected overlay (PPK / local_reference), keyed by the SAME ts. ---
        // The live point is never mutated; replay/scoring prefer corrected where it
        // exists (matches the own-server getHistory redraw + web-game Phase D).
        if (records.length > 0) {
            try {
                const corrSnap = await get(query(
                    ref(db, `boats/${boatId}/corrected`),
                    orderByKey(), startAt(String(startTime)), endAt(String(endTime))
                ))
                if (corrSnap.exists()) {
                    const corr = corrSnap.val() as Record<string, any>
                    let applied = 0
                    for (const rec of records) {
                        const c = corr[String(rec.timestamp)]
                        if (c && typeof c.lat === 'number' && typeof c.lon === 'number') {
                            rec.parsed = {
                                ...rec.parsed,
                                // keep the pre-correction fused position — the map's layer compare
                                // draws it next to the corrected line
                                pre_lat: rec.parsed?.lat,
                                pre_lon: rec.parsed?.lon,
                                lat: c.lat,
                                lon: c.lon,
                                acc_m: typeof c.sigma_m === 'number' ? c.sigma_m : rec.parsed?.acc_m,
                                corr: c.corr || 'corrected',
                            }
                            applied++
                        }
                    }
                    if (applied) console.log(`[fetchHistoricalData] Overlaid ${applied} corrected points`)
                }
            } catch { /* corrected overlay is best-effort */ }
        }

        // --- Fallback: old schema /boats/{id} ---
        if (records.length === 0) {
            const path = boatId === 'default' ? 'boats' : `boats/${boatId}`
            const boatRef = ref(db, path)
            const snapshot = await get(boatRef)

            if (snapshot.exists()) {
                const data = snapshot.val()

                // Skip if this is a new-schema node (has measurements/ or meta/)
                if (data.measurements || data.meta) {
                    // Already tried above, nothing more to do
                } else {
                    let lastDate: string | null = null
                    for (const key of Object.keys(data)) {
                        const record = data[key]
                        const { ts, dateStr } = extractTimestamp(key, record, lastDate)

                        if (dateStr) lastDate = dateStr

                        if (!isNaN(ts) && ts >= startTime && ts <= endTime) {
                            const dataStr = record.data || (typeof record === 'string' ? record : '')
                            if (dataStr && typeof dataStr === 'string') {
                                records.push({
                                    boatId,
                                    timestamp: ts,
                                    data: dataStr,
                                    parsed: parseBoatData(dataStr)
                                })
                            } else if (typeof record === 'object') {
                                records.push({
                                    boatId,
                                    timestamp: ts,
                                    parsed: structuredToParsed(record)
                                })
                            }
                        }
                    }
                }
            }
        }

        return records.sort((a, b) => a.timestamp - b.timestamp)
    } catch (error) {
        console.error('Error fetching historical data:', error)
        return []
    }
}

/**
 * Analyze boat data to find sessions (continuous periods of activity).
 * A gap of more than 10 minutes (600000ms) is considered a new session.
 */
export async function fetchDataSessions(boatId: string): Promise<import('../types').Session[]> {
    // Our server's real sessions (by session id) first; Firebase's gap-split timestamps only when it has none.
    const source = getDataSource()
    if (source !== 'firebase') {
        try {
            const body = await nidaGet(`/v1/boats/${encodeURIComponent(boatId)}/sessions?days=365`)
            const list = (body?.sessions ?? []) as any[]
            if (list.length > 0 || source === 'server') {
                return list.map(s => ({
                    id: String(s.session_id), name: s.name ?? undefined,
                    startTime: Number(s.start_ms), endTime: Number(s.end_ms),
                    duration: Number(s.end_ms) - Number(s.start_ms), dataPoints: Number(s.fixes) || 0,
                })).sort((a, b) => b.startTime - a.startTime)
            }
        } catch (e) { console.warn('[fetchDataSessions] our server failed, falling back to Firebase:', e) }
    }
    const db = getFirebaseDatabase()
    if (!db) return []

    try {
        const timestamps: number[] = []

        // --- Try new schema: /boats/{id}/measurements (keys are timestamps) ---
        const measRef = ref(db, `boats/${boatId}/measurements`)
        const measSnap = await get(measRef)
        if (measSnap.exists()) {
            const data = measSnap.val()
            for (const tsKey of Object.keys(data)) {
                const ts = parseInt(tsKey)
                if (!isNaN(ts)) timestamps.push(ts)
            }
        }

        // --- Fallback: old schema /boats/{id} ---
        if (timestamps.length === 0) {
            const path = boatId === 'default' ? 'boats' : `boats/${boatId}`
            const boatRef = ref(db, path)
            const snapshot = await get(boatRef)

            if (snapshot.exists()) {
                const data = snapshot.val()

                // Skip new-schema nodes
                if (!data.measurements && !data.meta) {
                    let lastDate: string | null = null
                    for (const key of Object.keys(data)) {
                        const record = data[key]
                        const { ts, dateStr } = extractTimestamp(key, record, lastDate)
                        if (dateStr) lastDate = dateStr
                        if (!isNaN(ts)) timestamps.push(ts)
                    }
                }
            }
        }

        if (timestamps.length === 0) return []

        timestamps.sort((a, b) => a - b)

        const sessions: import('../types').Session[] = []
        let currentStart = timestamps[0]
        let currentEnd = timestamps[0]
        let count = 1
        const GAP_THRESHOLD = 10 * 60 * 1000

        for (let i = 1; i < timestamps.length; i++) {
            const ts = timestamps[i]
            if (ts - currentEnd > GAP_THRESHOLD) {
                sessions.push({
                    id: `${currentStart}-${currentEnd}`,
                    startTime: currentStart,
                    endTime: currentEnd,
                    duration: currentEnd - currentStart,
                    dataPoints: count
                })
                currentStart = ts
                currentEnd = ts
                count = 1
            } else {
                currentEnd = ts
                count++
            }
        }

        sessions.push({
            id: `${currentStart}-${currentEnd}`,
            startTime: currentStart,
            endTime: currentEnd,
            duration: currentEnd - currentStart,
            dataPoints: count
        })

        return sessions.sort((a, b) => b.startTime - a.startTime)

    } catch (error) {
        console.error('Error fetching sessions:', error)
        return []
    }
}
